import { createEntityAdapter } from '@reduxjs/toolkit';
import chronosApi from '@chronos/api-client/chronosApi';
import { chronosBaseApi } from './chronosBaseApi';

const boardMemberPermissionsAdapter = createEntityAdapter();
export const initialPermissionsState =
  boardMemberPermissionsAdapter.getInitialState();
const apiWithTag = chronosBaseApi.enhanceEndpoints({
  addTagTypes: ['Settings'],
});
const settingsApi = apiWithTag.injectEndpoints({
  endpoints: (build) => ({
    getTrelloBoardMemberSettings: build.query({
      query: ({ boardId }) => ({
        url: `/trello/settings/boardMemberSettings`,
        method: 'GET',
        params: {
          boardId,
        },
      }),
    }),
    getTrelloBoardAdminSettings: build.query({
      query: ({ boardId }) => ({
        url: `/trello/settings/boardAdminSettings`,
        method: 'GET',
        params: {
          boardId,
        },
      }),
    }),
    getTrelloBoardMemberPermissions: build.query({
      query: ({ boardId, accountId }) => ({
        url: `/trello/settings/boardMemberPermissions`,
        method: 'GET',
        params: {
          boardId,
          accountId,
        },
      }),
      transformResponse: (responseData) => {
        return boardMemberPermissionsAdapter.setAll(
          initialPermissionsState,
          responseData
        );
      },
    }),
    getBillingInfo: build.query({
      queryFn: async ({ organizationId }) => {
        const billingInfo = await chronosApi.getBillingInfo({
          body: {
            organizationId,
          },
        });
        return { data: billingInfo };
      },
    }),
    activateAccount: build.mutation({
      query: ({ body }) => ({
        url: '/trello/settings/activateAccount',
        method: 'POST',
        body,
      }),
      // Fires two times by unknown reason
      async onQueryStarted({ body }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          settingsApi.util.updateQueryData(
            'getBillingInfo',
            { organizationId: body.organizationId },
            (draft) => {
              if (
                body.action === 'activate' &&
                !(draft.activeAccounts ?? []).includes(body.accountId)
              ) {
                if (draft.activeAccounts) {
                  draft.activeAccounts.push(body.accountId);
                } else {
                  draft.activeAccounts = [body.accountId];
                }
              }
              if (
                body.action === 'deActivate' &&
                (draft.activeAccounts ?? []).includes(body.accountId)
              ) {
                if (draft.activeAccounts) {
                  draft.activeAccounts = draft.activeAccounts.filter(
                    (id) => id !== body.accountId
                  );
                }
              }
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    saveBoardMemberSettings: build.mutation({
      query: ({ body }) => ({
        url: '/trello/settings/boardMemberSettings',
        method: 'POST',
        body,
      }),
      async onQueryStarted({ body }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          settingsApi.util.updateQueryData(
            'getTrelloBoardMemberSettings',
            { boardId: body.boardId },
            (draft) => {
              Object.keys(body).forEach((key) => {
                draft[key] = body[key];
              });
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    saveBoardAdminSettings: build.mutation({
      query: ({ body }) => ({
        url: '/trello/settings/boardAdminSettings',
        method: 'POST',
        body,
      }),
      async onQueryStarted({ body }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          settingsApi.util.updateQueryData(
            'getTrelloBoardAdminSettings',
            { boardId: body.boardId },
            (draft) => {
              Object.keys(body).forEach((key) => {
                draft[key] = body[key];
              });
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    saveBoardMemberPermissions: build.mutation({
      query: ({ body }) => ({
        url: '/trello/settings/boardMemberPermissions',
        method: 'POST',
        body,
      }),
      async onQueryStarted({ body }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          settingsApi.util.updateQueryData(
            'getTrelloBoardMemberPermissions',
            { boardId: body.boardId },
            (draft) => {
              boardMemberPermissionsAdapter.setOne(draft, body);
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetTrelloBoardMemberSettingsQuery,
  useGetTrelloBoardAdminSettingsQuery,
  useGetTrelloBoardMemberPermissionsQuery,
  useGetBillingInfoQuery,
  useSaveBoardMemberSettingsMutation,
  useSaveBoardAdminSettingsMutation,
  useSaveBoardMemberPermissionsMutation,
  useActivateAccountMutation,
} = settingsApi;

export default settingsApi;
